import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FundListSummary } from '@nemo/api';
import { useMemo, useState } from 'react';
import { Archive, List, Plus } from 'lucide-react';
import { useLocation } from 'wouter';
import { actionsCell, type GridAction } from '../grid/cells/index.js';
import { orderedColumns, sortRows, type GridColumnDef, type Sort } from '../grid/columns.js';
import { DataGrid } from '../grid/DataGrid.js';
import { DensityToggle } from '../grid/DensityToggle.js';
import { text } from '../grid/fundColumns.js';
import { useGridPreferences } from '../grid/useGridPreferences.js';
import { useTRPC } from '../trpc.js';
import { SegmentedControl, type Segment } from '../ui/SegmentedControl.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { NewListModal } from './NewListModal.js';
import { formatWhen } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';

const PREFERENCES_KEY = 'nemo.grid.lists.v1';

/** The lists in use, or the ones deleted and waiting to be restored or purged. */
type Shelf = 'current' | 'deleted';

const CURRENT_ACTIONS: readonly GridAction[] = [
  { id: 'open', label: 'Open' },
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'delete', label: 'Delete', tone: 'danger' },
];

const DELETED_ACTIONS: readonly GridAction[] = [
  { id: 'restore', label: 'Restore' },
  { id: 'purge', label: 'Delete permanently', tone: 'danger' },
];

function columnsFor(shelf: Shelf): readonly GridColumnDef<FundListSummary>[] {
  const when = shelf === 'current' ? 'updatedAt' : 'deletedAt';
  return [
    {
      id: 'name',
      title: 'Name',
      width: 280,
      cell: (list) => ({ ...text(list.name, false), cursor: 'pointer' }),
      text: (list) => list.name,
      sortKey: (list) => list.name.toLowerCase(),
    },
    {
      id: 'for',
      title: 'For',
      width: 220,
      cell: (list) => text(list.portfolioCompanyName ?? '', false),
      text: (list) => list.portfolioCompanyName ?? '',
      sortKey: (list) => list.portfolioCompanyName?.toLowerCase() ?? null,
    },
    {
      id: 'funds',
      title: 'Funds',
      width: 80,
      cell: (list) => ({ ...text(String(list.itemCount), false), contentAlign: 'right' }),
      text: (list) => String(list.itemCount),
      sortKey: (list) => list.itemCount,
    },
    {
      id: 'when',
      title: shelf === 'current' ? 'Updated' : 'Deleted',
      width: 150,
      cell: (list) => text(formatWhen(list[when]), false),
      text: (list) => formatWhen(list[when]),
      sortKey: (list) => list[when] ?? null,
    },
    {
      id: 'actions',
      title: '',
      width: shelf === 'current' ? 230 : 210,
      cell: () => actionsCell(shelf === 'current' ? CURRENT_ACTIONS : DELETED_ACTIONS),
      text: () => '',
    },
  ];
}

/**
 * Every list the team has curated for a founder. A list is usually started
 * from the funds grid (a filter, then "Save as a list"); this page is where
 * they are found again, copied for the next raise, or thrown away; a deleted
 * list can be restored from Deleted.
 */
export function ListsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const lists = useQuery(trpc.lists.list.queryOptions());
  const deletedLists = useQuery(
    trpc.lists.list.queryOptions(
      { includeDeleted: true },
      {
        select: (all) => all.filter((list) => list.deletedAt !== null),
      },
    ),
  );
  const [deleting, setDeleting] = useState<FundListSummary | null>(null);
  const [purging, setPurging] = useState<FundListSummary | null>(null);
  const [shelf, setShelf] = useState<Shelf>('current');
  const [sort, setSort] = useState<Sort | null>(null);
  const preferences = useGridPreferences(PREFERENCES_KEY);
  const [naming, setNaming] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: trpc.lists.list.queryKey() });
  const create = useMutation(
    trpc.lists.create.mutationOptions({
      onSuccess: (answer) => {
        void refresh();
        navigate(`/lists/${answer.id}`);
      },
      onError: (error) => toast(error.message),
    }),
  );
  const duplicate = useMutation(
    trpc.lists.duplicate.mutationOptions({
      onSuccess: (answer) => {
        void refresh();
        navigate(`/lists/${answer.id}`);
      },
      onError: (error) => toast(error.message),
    }),
  );
  const restore = useMutation(
    trpc.lists.restore.mutationOptions({
      onSuccess: () => void refresh(),
      onError: (error) => toast(error.message),
    }),
  );
  const purge = useMutation(
    trpc.lists.purge.mutationOptions({
      onSuccess: () => void refresh(),
      onError: (error) => toast(error.message),
    }),
  );
  const remove = useMutation(
    trpc.lists.delete.mutationOptions({
      onSuccess: () => void refresh(),
      onError: (error) => toast(error.message),
    }),
  );

  const shown = (shelf === 'current' ? lists.data : deletedLists.data) ?? [];
  const columns = useMemo(
    () => orderedColumns(columnsFor(shelf), preferences),
    [shelf, preferences],
  );
  const rows = useMemo(() => sortRows(shown, columns, sort, undefined), [shown, columns, sort]);
  const deletedCount = deletedLists.data?.length ?? 0;

  const shelves: readonly Segment<Shelf>[] = [
    { value: 'current', label: 'Lists', icon: List },
    { value: 'deleted', label: `Deleted (${deletedCount})`, icon: Archive },
  ];

  function act(list: FundListSummary, action: string): void {
    switch (action) {
      case 'open':
        navigate(`/lists/${list.id}`);
        return;
      case 'duplicate':
        if (!duplicate.isPending) duplicate.mutate({ id: list.id });
        return;
      case 'delete':
        setDeleting(list);
        return;
      case 'restore':
        if (!restore.isPending) restore.mutate({ id: list.id });
        return;
      case 'purge':
        setPurging(list);
        return;
    }
  }

  const failed = lists.isError ? lists.error.message : null;

  return (
    <div className="page">
      <div className="toolbar">
        <strong className="count">
          {lists.data === undefined
            ? 'Lists'
            : `${lists.data.length} list${lists.data.length === 1 ? '' : 's'}`}
        </strong>
        <span className="subtle">
          Filter the funds and save the rows as a list, or select rows and add them to one.
        </span>
        <span className="spacer" />
        {(deletedCount > 0 || shelf === 'deleted') && (
          <SegmentedControl
            label="Which lists"
            value={shelf}
            options={shelves}
            onChange={(next) => {
              setShelf(next);
              setSort(null);
            }}
          />
        )}
        <DensityToggle density={preferences.density} onChange={preferences.setDensity} />
        <button className="button" type="button" onClick={() => setNaming(true)}>
          <Plus aria-hidden="true" />
          New list
        </button>
      </div>
      <DataGrid
        rows={rows}
        columns={columns}
        context={undefined}
        rowId={(list) => list.id}
        preferences={preferences}
        sort={{ value: sort, onChange: setSort }}
        open={{
          openId: null,
          onOpen: (list) => navigate(`/lists/${list.id}`),
          onClose: () => undefined,
          opensFrom: (columnId) => columnId !== 'actions',
        }}
        onAction={act}
        rowMenu={{
          label: 'List',
          items: (list) =>
            (shelf === 'current' ? CURRENT_ACTIONS : DELETED_ACTIONS).map((action) => ({
              label: action.label,
              danger: action.tone === 'danger',
              onSelect: () => act(list, action.id),
            })),
        }}
        freezeColumns={1}
        empty={
          <span className="subtle">
            {failed !== null
              ? `Could not load the lists: ${failed}`
              : lists.isPending
                ? 'Loading the lists…'
                : shelf === 'current'
                  ? 'No lists yet.'
                  : 'Nothing deleted.'}
          </span>
        }
      />
      {naming && (
        <NewListModal
          pending={create.isPending}
          onCreate={(list) => create.mutate({ ...list, fundIds: [] })}
          onCancel={() => setNaming(false)}
        />
      )}
      {deleting !== null && (
        <ConfirmModal
          title={`Delete “${deleting.name}”?`}
          body="The list moves to Deleted, with its notes and intros, and can be restored."
          confirmLabel="Delete list"
          danger
          onConfirm={() => {
            remove.mutate({ id: deleting.id });
            setDeleting(null);
          }}
          onCancel={() => setDeleting(null)}
        />
      )}
      {purging !== null && (
        <ConfirmModal
          title={`Delete “${purging.name}” permanently?`}
          body="The list goes for good, with its notes and intros. The funds stay as they are."
          confirmLabel="Delete permanently"
          danger
          onConfirm={() => {
            purge.mutate({ id: purging.id });
            setPurging(null);
          }}
          onCancel={() => setPurging(null)}
        />
      )}
    </div>
  );
}
